import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { extractErrorMessage } from '../api/errorUtils';
import axiosInstance from '../api/axiosInstance';
import { showNotification } from './notificationSlice';
import { fetchUserPermissions } from './authSlice';

const ENDPOINTS = {
  getModulePermissions: 'techsavanna_pos.api.module_permission_api.get_module_permissions',
  setModulePermissions: 'techsavanna_pos.api.module_permission_api.set_module_permissions',
  getAppModules: 'techsavanna_pos.api.module_permission_api.get_pos_modules',
};

const extractResponseData = (response) => {
  if (response.data?.message && typeof response.data.message === 'object') {
    return response.data.message;
  }
  return response.data?.message || response.data;
};

const createFormData = (data) => {
  const formData = new FormData();
  Object.entries(data).forEach(([key, value]) => {
    if (value !== null && value !== undefined) {
      if (typeof value === 'object' && !(value instanceof File)) {
        formData.append(key, JSON.stringify(value));
      } else if (typeof value === 'boolean') {
        formData.append(key, value ? 1 : 0);
      } else {
        formData.append(key, value);
      }
    }
  });
  return formData;
};

export const getModulePermissions = createAsyncThunk(
  'modulePermission/getModulePermissions',
  async (roleName, { dispatch, rejectWithValue }) => {
    try {
      const formData = createFormData({ role_name: roleName });
      const response = await axiosInstance.post(ENDPOINTS.getModulePermissions, formData, {
        transformRequest: [(data, headers) => {
          delete headers['Content-Type'];
          return data;
        }],
      });
      const data = extractResponseData(response);
      return { roleName, permissions: data.data || {} };
    } catch (error) {
      const errorMessage = extractErrorMessage(error);
      dispatch(showNotification({ message: errorMessage, severity: 'error', title: 'Failed to fetch module permissions' }));
      return rejectWithValue(errorMessage);
    }
  }
);

export const setModulePermissions = createAsyncThunk(
  'modulePermission/setModulePermissions',
  async ({ roleName, modulePermissions }, { dispatch, rejectWithValue }) => {
    try {
      const formData = createFormData({
        role_name: roleName,
        module_permissions: modulePermissions,
      });
      const response = await axiosInstance.post(ENDPOINTS.setModulePermissions, formData, {
        transformRequest: [(data, headers) => {
          delete headers['Content-Type'];
          return data;
        }],
      });
      const data = extractResponseData(response);
      dispatch(showNotification({
        message: data.message || 'Module permissions saved successfully',
        severity: 'success',
        title: 'Success',
      }));
      // Await so callers see updated permissions immediately after the thunk resolves.
      await dispatch(fetchUserPermissions());
      return { roleName, permissions: modulePermissions };
    } catch (error) {
      const errorMessage = extractErrorMessage(error);
      dispatch(showNotification({ message: errorMessage, severity: 'error', title: 'Failed to save module permissions' }));
      return rejectWithValue(errorMessage);
    }
  }
);

export const getAppModules = createAsyncThunk(
  'modulePermission/getAppModules',
  async (_, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post(ENDPOINTS.getAppModules);
      const data = extractResponseData(response);
      return { modules: data.data || [] };
    } catch (error) {
      return rejectWithValue(extractErrorMessage(error));
    }
  }
);

const initialState = {
  modulePermissions: {},
  availableModules: [],
  isLoading: false,
  isSaving: false,
  error: null,
};

const modulePermissionSlice = createSlice({
  name: 'modulePermission',
  initialState,
  reducers: {
    clearModulePermissions: (state) => {
      state.modulePermissions = {};
    },
    clearAvailableModules: (state) => {
      state.availableModules = [];
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getAppModules.fulfilled, (state, action) => {
        state.availableModules = action.payload.modules;
      })
      .addCase(getModulePermissions.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getModulePermissions.fulfilled, (state, action) => {
        state.isLoading = false;
        state.modulePermissions[action.payload.roleName] = action.payload.permissions;
      })
      .addCase(getModulePermissions.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(setModulePermissions.pending, (state) => {
        state.isSaving = true;
        state.error = null;
      })
      .addCase(setModulePermissions.fulfilled, (state, action) => {
        state.isSaving = false;
        state.modulePermissions[action.payload.roleName] = action.payload.permissions;
      })
      .addCase(setModulePermissions.rejected, (state, action) => {
        state.isSaving = false;
        state.error = action.payload;
      });
  },
});

export const { clearModulePermissions, clearAvailableModules } = modulePermissionSlice.actions;
export default modulePermissionSlice.reducer;
